import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useReducer, useRef } from 'react';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Helmet } from 'react-helmet';
import { SearchEntityType } from '@tribeplatform/gql-client/types';
import { useEmbed, useSearch } from '@tribeplatform/react-sdk/hooks';
import { useDocument } from '@tribeplatform/react-ui-kit/hooks';
import { useI18n, useStaticIntl } from '../i18n';
import { cleanSearchQuery } from '../utils/search';
import { ReducerActionKind, } from './@types';
import { AttachmentPickerButton, } from './components/AttachmentPickerButton';
import { ComposerConfigContext } from './ComposerConfigContext';
import { ComposerContext } from './ComposerContext';
import { DefaultExtensionConfig } from './config/constants';
import { Attachment as AttachmentExtension } from './extensions/attachment';
import { CodeBlock } from './extensions/code-block';
import { Embed as EmbedExtension } from './extensions/embed';
import { Emoji } from './extensions/emoji';
import { CustomHeading } from './extensions/heading';
import { AsyncImage } from './extensions/image';
import { CustomLink } from './extensions/link';
import { Mention } from './extensions/mention';
import { mention } from './extensions/mention/options';
import { SlashMenu } from './extensions/slash-menu';
import { slashMenuCommand } from './extensions/slash-menu/click-handler';
import { slashMenuItemsFactory, MenuTypes } from './extensions/slash-menu/items';
import { Video } from './extensions/video';
import { useAddAttachmentToEditor } from './hooks/useAddAttachmentToEditor';
import { useHandleFiles } from './hooks/useHandleFiles';
import { useHandlePaste } from './hooks/useHandlePaste';
const initialDialogsState = {
    link: false,
    embed: false,
    video: false,
    audio: false,
    image: false,
    giphy: false,
    embedItem: undefined,
    textFormatMenu: false,
};
function reducer(state, action) {
    switch (action.type) {
        case ReducerActionKind.SET:
            return { ...state, [action.key]: action.value };
        case ReducerActionKind.SET_EMBED_ITEM:
            return { ...state, embedItem: action.embedItem };
        default:
            throw state;
    }
}
export const ComposerProvider = ({ children, content, embeds, attachments, mentions, editorOptions, cachedMentions, placeholder, extensionConfig = DefaultExtensionConfig, }) => {
    const intl = useI18n();
    const { document } = useDocument();
    const { asyncQuery: embed } = useEmbed();
    const { asyncQuery: search } = useSearch({ fields: 'all' });
    const isTouched = useRef(false);
    const attachmentControlRef = useRef();
    const [state, dispatch] = useReducer(reducer, initialDialogsState);
    const toggleDialog = useCallback((key, value) => {
        dispatch({ type: ReducerActionKind.SET, key, value });
    }, []);
    const onSlashMenuItemClick = useCallback(({ editor, props, range }) => {
        var _a;
        let _range = range;
        if (!_range) {
            _range = {
                from: editor.view.state.selection.from,
                to: editor.view.state.selection.to,
            };
        }
        slashMenuCommand({ props, editor, range: _range });
        switch (props.type) {
            case MenuTypes.Giphy:
                toggleDialog('giphy', true);
                break;
            case MenuTypes.Video:
                toggleDialog('video', true);
                break;
            case MenuTypes.Audio:
                toggleDialog('audio', true);
                break;
            case MenuTypes.Embed:
                toggleDialog('embed', true);
                dispatch({
                    type: ReducerActionKind.SET_EMBED_ITEM,
                    embedItem: props,
                });
                break;
            case MenuTypes.Link:
                toggleDialog('link', true);
                break;
            case MenuTypes.Attachment:
                (_a = attachmentControlRef.current) === null || _a === void 0 ? void 0 : _a.open();
                break;
            case MenuTypes.Image:
                toggleDialog('image', true);
                break;
            default:
        }
    }, [toggleDialog]);
    const { handlePaste } = useHandlePaste();
    const getItems = useStaticIntl(slashMenuItemsFactory);
    const slashMenuItems = getItems(extensionConfig);
    const extensions = [
        StarterKit.configure({
            heading: false,
            codeBlock: false,
        }),
        Underline,
        Placeholder.configure({
            placeholder: placeholder ||
                intl.$t({
                    id: 'Composer.ComposerProvider.DefaultPlaceholderText',
                    defaultMessage: "What are your thoughts? (Type ' / ' to add images, files, or links)",
                }),
        }),
        CustomLink.configure({
            openOnClick: false,
            onPaste: async (m, editor) => {
                var _a, _b, _c;
                const { from } = editor.state.selection;
                const href = (_b = (_a = m === null || m === void 0 ? void 0 : m[0]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.href;
                const text = (_c = m === null || m === void 0 ? void 0 : m[0]) === null || _c === void 0 ? void 0 : _c.text;
                const oembed = await embed({ url: href });
                if (oembed.html) {
                    editor
                        .chain()
                        .deleteRange({ from, to: from + text.length })
                        .setEmbed({ embed: oembed })
                        .focus()
                        .run();
                }
            },
            HTMLAttributes: {
                class: 'text-interactive hover:text-interactive-hovered',
            },
        }),
        SlashMenu.configure({
            command: onSlashMenuItemClick,
            suggestion: {
                items: async ({ query }) => {
                    if (!query || query.length <= 1) {
                        return slashMenuItems;
                    }
                    return slashMenuItems.filter(item => { var _a; return (_a = item === null || item === void 0 ? void 0 : item.label) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(query.toLowerCase()); });
                },
            },
        }),
    ];
    if (extensionConfig.heading) {
        extensions.push(CustomHeading.configure({
            levels: [2, 3],
        }));
    }
    if (extensionConfig.embed) {
        extensions.push(EmbedExtension.configure({
            embeds,
        }));
    }
    if (extensionConfig.attachment) {
        extensions.push(AttachmentExtension.configure({
            attachments,
        }));
    }
    if (extensionConfig.image) {
        extensions.push(AsyncImage);
    }
    if (extensionConfig.emoji) {
        extensions.push(Emoji);
    }
    if (extensionConfig.video) {
        extensions.push(Video);
    }
    if (extensionConfig.mention) {
        extensions.push(Mention.configure({
            HTMLAttributes: {
                class: 'text-interactive hover:text-interactive-hovered',
            },
            mentions,
            suggestion: mention({
                document,
                cachedMentions,
                search: ({ query: dirtyQuery }) => {
                    const query = cleanSearchQuery(dirtyQuery);
                    if (!query) {
                        return new Promise(res => res(undefined));
                    }
                    return search({
                        input: {
                            query: `${query} +for:member`,
                        },
                    }).then(result => {
                        var _a;
                        const members = result === null || result === void 0 ? void 0 : result.hits.find(hit => hit.entityType === SearchEntityType.member);
                        return (_a = members === null || members === void 0 ? void 0 : members.hits) === null || _a === void 0 ? void 0 : _a.map(m => {
                            var _a;
                            return ({
                                id: m.entityId,
                                title: m.title,
                                icon: (_a = m === null || m === void 0 ? void 0 : m.media) === null || _a === void 0 ? void 0 : _a.url,
                            });
                        });
                    });
                },
            }),
        }));
    }
    if (extensionConfig.code) {
        extensions.push(CodeBlock);
    }
    const editor = useEditor({
        content,
        extensions,
        editorProps: {
            handlePaste,
        },
        ...editorOptions,
    });
    const { addAttachment } = useAddAttachmentToEditor({ editor });
    const defaultHandleAttachment = async (files) => {
        await Promise.all(files.map(file => addAttachment(file)));
    };
    const { onFileUpload, loading } = useHandleFiles({
        editor,
        handleAttachmentSelect: defaultHandleAttachment,
    });
    useEffect(() => {
        const onUpdate = () => {
            isTouched.current = true;
        };
        if (editor) {
            editor.on('update', onUpdate);
            return () => {
                editor.off('update', onUpdate);
            };
        }
    }, [editor]);
    return (_jsxs(_Fragment, { children: [_jsx(Helmet, { children: _jsx("script", { src: "//cdn.iframe.ly/embed.js", async: true }, void 0) }, void 0), _jsx(ComposerConfigContext.Provider, { value: { extensions: extensionConfig }, children: _jsxs(ComposerContext.Provider, { value: {
                        withProvider: true,
                        isTouched: isTouched.current,
                        editor,
                        onFileUpload,
                        onSlashMenuItemClick,
                        toggleDialog,
                        state: {
                            ...state,
                            loading,
                        },
                    }, children: [children, _jsx("span", { className: "hidden", children: _jsx(AttachmentPickerButton, { ref: attachmentControlRef }, void 0) }, void 0)] }, void 0) }, void 0)] }, void 0));
};
//# sourceMappingURL=ComposerProvider.js.map